import { useCallback, useState } from 'react';

import { SOMETHING_ELSE_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type UseWorkspaceAgentSelectChoiceParams = {
  /** Options already picked when the dock opens; pick one keeps only the first. Absent means none. */
  defaultIndexes: readonly number[] | undefined;
  isMulti: boolean;
  options: readonly string[];
};

/** Chip selection and the Something else text for a select dock. Something else is always the last option. */
export const useWorkspaceAgentSelectChoice = ({
  defaultIndexes,
  isMulti,
  options,
}: UseWorkspaceAgentSelectChoiceParams) => {
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>(() => {
    const sorted = [...(defaultIndexes ?? [])].sort((left, right) => left - right);

    return isMulti ? sorted : sorted.slice(0, 1);
  });
  const [customText, setCustomText] = useState('');

  const somethingElseIndex = options.length - 1;
  const trimmedCustomText = customText.trim();
  const choseCustom = selectedIndexes.includes(somethingElseIndex);
  const customReady = trimmedCustomText.length > 0 && trimmedCustomText.length <= SOMETHING_ELSE_MAX_LENGTH;
  const canSubmit =
    selectedIndexes.length > 0 && (!choseCustom || customReady) && (isMulti || selectedIndexes.length === 1);

  const handleOptionClick = useCallback(
    (index: number) => {
      if (!isMulti) {
        setSelectedIndexes([index]);
        return;
      }

      setSelectedIndexes((current) =>
        current.includes(index)
          ? current.filter((item) => item !== index)
          : [...current, index].sort((left, right) => left - right),
      );
    },
    [isMulti],
  );

  const dismissCustom = useCallback(() => {
    setSelectedIndexes((current) => current.filter((item) => item !== somethingElseIndex));
    setCustomText('');
  }, [somethingElseIndex]);

  return {
    canSubmit,
    choseCustom,
    customText,
    dismissCustom,
    handleOptionClick,
    selectedIndexes,
    setCustomText,
    trimmedCustomText,
  };
};
